/**
 * Helpers for Roles factory methods
 */

import type { IBaseAdmin } from "../../BaseAdminFactory";
import type { Role } from "le-coffre-resources/dist/Admin";
import type { QueryPayload } from "../../../../types";
import type { IGetRolesParams, IPutRoleParams, IPostRoleParams } from "../Roles";
import { buildRolesCreatePayload, buildRolesUpdatePayload } from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";

/**
 * Create GET method for roles
 */
export function createGetMethod(baseAdmin: IBaseAdmin, baseUrl: string) {
	return async (q?: IGetRolesParams): Promise<Role[]> => {
		const url = new URL(baseUrl);
		if (q) {
			baseAdmin.appendJsonSearchParams(url, { q });
		}
		try {
			return await baseAdmin.getRequest<Role[]>(url);
		} catch (err) {
			baseAdmin.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create POST method for roles
 */
export function createPostMethod(baseAdmin: IBaseAdmin, baseUrl: string) {
	return async (body: IPostRoleParams): Promise<Role> => {
		const url = new URL(baseUrl);
		try {
			const payload = buildRolesCreatePayload(body);
			return await baseAdmin.postRequest<Role>(url, payload);
		} catch (err) {
			baseAdmin.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create get one method for roles
 */
export function createGetOneMethod(baseAdmin: IBaseAdmin, baseUrl: string) {
	return async (q?: IGetRolesParams): Promise<Role | null> => {
		const url = new URL(baseUrl);
		if (q) {
			baseAdmin.appendJsonSearchParams(url, { q });
		}
		try {
			const res = await baseAdmin.getRequest<Role[]>(url);
			if (!res) return null;
			if (res.length > 1) throw new Error("More than one role found");
			return res[0] ? res[0] : null;
		} catch (err) {
			baseAdmin.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create GET by UID method for roles
 */
export function createGetByUidMethod(baseAdmin: IBaseAdmin, baseUrl: string) {
	return async (uid: string, q?: QueryPayload): Promise<Role> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		if (q) baseAdmin.appendJsonSearchParams(url, q);
		try {
			return await baseAdmin.getRequest<Role>(url);
		} catch (err) {
			baseAdmin.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create PUT method for roles
 */
export function createPutMethod(baseAdmin: IBaseAdmin, baseUrl: string) {
	return async (uid: string, body: IPutRoleParams): Promise<Role> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		try {
			const payload = buildRolesUpdatePayload(body);
			return await baseAdmin.putRequest<Role>(url, payload);
		} catch (err) {
			baseAdmin.onError(err);
			return Promise.reject(err);
		}
	};
}
